import { useMutation } from '@tanstack/react-query'
import React, { useState } from 'react'
import invariant from 'tiny-invariant'

import { useCompassMetadataOrFail } from '@/components/flows/CompassFlow/hooks/useCompassMetadataOrFail'
import { MeetingDetails } from '@/components/integration/nylas/scheduler/types'
import {
  defaultServiceAreaIds,
  isServiceAreaIdsEmpty,
  ServiceAreaIds,
} from '@/components/misc/CompassPartner/helpers'
import CalendlyModal from '@/components/misc/RequestCompanyCallbackButton/CalendlyModal'
import NonCalendlyModal from '@/components/misc/RequestCompanyCallbackButton/NonCalendlyModal'
import RequestCallbackModal from '@/components/misc/RequestCompanyCallbackButton/RequestCallbackModal'
import SchedulerModal from '@/components/misc/RequestCompanyCallbackButton/SchedulerModal'
import ShareCompassReportModal from '@/components/misc/RequestCompanyCallbackButton/ShareCompassReportModal'
import { RequestCallbackFormValues } from '@/components/misc/RequestReferralCallbackForm'
import { Button } from '@/components/ui/button'
import { useClientMetadata } from '@/hooks/useClientMetadata'
import { useCurrentClientUser } from '@/hooks/useCurrentClientUser'
import useNotyf from '@/hooks/useNotyf'
import { API_PATH_INTERNAL, COMPASS_REPORT_STATUS } from '@/lib/constants'
import {
  getOrFail,
  isCalendlyUrl,
  toSafeHttpUrl,
} from '@/lib/helpers/helperFunctions'
import companyService from '@/lib/services/companyService'
import constraintViolationService from '@/lib/services/constraintViolationService'
import errorTypeService from '@/lib/services/errorTypeService'
import { CompassPartnerOrSponsorDetails } from '@/types/misc'
import {
  CompanyRequestCallRequest,
  CreateCompanySelfReferralRequest,
} from '@/types/requests/companies'
import { MeetingRequest } from '@/types/requests/meetings'
import {
  CompassReportItem,
  LatestCompassReportItem,
} from '@/types/responses/compass-reports'

interface Props {
  compassReport: CompassReportItem | LatestCompassReportItem
  compassPartner: CompassPartnerOrSponsorDetails
  serviceAreaIds?: ServiceAreaIds
  isCreateReferral?: boolean
  onHoverEnter?: () => void
  onHoverLeave?: () => void
}

type ModalType =
  | 'compassSharing'
  | 'requestCallback'
  | 'bookMeetingViaCalendly'
  | 'bookMeetingNonCalendly'
  | 'bookMeetingViaScheduler'

const MODAL_TYPE: Record<ModalType, ModalType> = {
  compassSharing: 'compassSharing',
  requestCallback: 'requestCallback',
  bookMeetingViaCalendly: 'bookMeetingViaCalendly',
  bookMeetingNonCalendly: 'bookMeetingNonCalendly',
  bookMeetingViaScheduler: 'bookMeetingViaScheduler',
}

const RequestCompanyCallbackButton: React.FC<Props> = ({
  compassReport,
  compassPartner,
  serviceAreaIds,
  isCreateReferral = true,
  onHoverEnter,
  onHoverLeave,
}) => {
  const notyf = useNotyf()
  const [isSharingCompassData, setIsSharingCompassData] = useState(false)
  const [activeModal, setActiveModal] = useState<ModalType | null>(null)
  const [companyIdsReferredToAlready, setCompanyIdsReferredToAlready] =
    useState<number[]>([])
  const clientMetadata = useClientMetadata()
  const compassMetadata = useCompassMetadataOrFail()
  const sponsorCompanyId =
    clientMetadata?.sponsorCompany?.id ?? compassMetadata.company?.id
  const currentClientUser = useCurrentClientUser()

  const latestCompassReportIri =
    compassReport?.['@id'] &&
    compassReport.status === COMPASS_REPORT_STATUS.COMPLETE
      ? compassReport['@id']
      : clientMetadata?.latestCompletedCompassReport

  const requireConsent = compassPartner.requireConsent ?? true
  const requireDataSharingOptIn =
    sponsorCompanyId !== compassPartner.id && requireConsent

  invariant(
    sponsorCompanyId,
    'Expected either sponsorCompany or compass company to be set',
  )

  const sponsorCompanyIri = `${API_PATH_INTERNAL}/companies/${sponsorCompanyId}`

  const hasAlreadyBeenReferredTo = companyIdsReferredToAlready.includes(
    compassPartner.id,
  )

  const scheduleCallUrl =
    toSafeHttpUrl(compassPartner.scheduleClientCallUrl) ?? ''

  function handleCtaClick() {
    if (hasAlreadyBeenReferredTo) {
      return
    }

    if (requireDataSharingOptIn) {
      setActiveModal(MODAL_TYPE.compassSharing)
      return
    }

    setIsSharingCompassData(true)
    showBookCallModal()
  }

  function showBookCallModal() {
    if (compassPartner.hasUsableCalendars && isCreateReferral) {
      setActiveModal(MODAL_TYPE.bookMeetingViaScheduler)
      return
    }

    if (isCalendlyUrl(scheduleCallUrl)) {
      setActiveModal(MODAL_TYPE.bookMeetingViaCalendly)
      return
    }

    if (scheduleCallUrl) {
      setActiveModal(MODAL_TYPE.bookMeetingNonCalendly)
      return
    }

    setActiveModal(MODAL_TYPE.requestCallback)
  }

  function hideModal() {
    setActiveModal(null)
  }

  const requestCallbackMutation = useMutation({
    mutationFn: (values: RequestCallbackFormValues) => {
      const request: CompanyRequestCallRequest = {
        token: compassReport?.token,
        callbackTelephone: values.telephone,
        callbackTimes: values.times,
      }

      return companyService.createCompanyRequestCallRequest(
        compassPartner.id,
        request,
      )
    },
    onSuccess: handleReferralCreatedSuccess,
    onError: handleReferralCreatedError,
  })

  const requestReferralCallbackMutation = useMutation({
    mutationFn: (values: RequestCallbackFormValues) => {
      const request: CreateCompanySelfReferralRequest = {
        token: compassReport?.token,
        sponsorCompany: sponsorCompanyIri,
        shareData: isSharingCompassData || !requireDataSharingOptIn,
        compassReport:
          isSharingCompassData && latestCompassReportIri
            ? latestCompassReportIri
            : undefined,
        serviceAreaIds:
          serviceAreaIds && !isServiceAreaIdsEmpty(serviceAreaIds)
            ? serviceAreaIds
            : defaultServiceAreaIds(),
        callbackTelephone: values.telephone,
        callbackTimes: values.times,
        isReferringViaCompass: true,
      }

      return companyService.createCompanySelfReferral(
        compassPartner.id,
        request,
      )
    },
    onSuccess: handleReferralCreatedSuccess,
    onError: handleReferralCreatedError,
  })

  const scheduleCallMutation = useMutation({
    mutationFn: async (variables: { meeting: MeetingRequest } | void) => {
      const meeting = variables?.meeting

      if (hasAlreadyBeenReferredTo || !isCreateReferral) {
        return
      }

      const request: CreateCompanySelfReferralRequest = {
        token: compassReport?.token,
        sponsorCompany: sponsorCompanyIri,
        shareData: isSharingCompassData || !requireDataSharingOptIn,
        compassReport:
          isSharingCompassData && latestCompassReportIri
            ? latestCompassReportIri
            : undefined,
        serviceAreaIds:
          serviceAreaIds && !isServiceAreaIdsEmpty(serviceAreaIds)
            ? serviceAreaIds
            : defaultServiceAreaIds(),
        meeting,
        isReferringViaCompass: true,
      }

      return (
        await companyService.createCompanySelfReferral(
          compassPartner.id,
          request,
        )
      ).meeting
    },
    onSuccess: handleReferralCreatedSuccess,
    onError: (error) => {
      if (errorTypeService.isConflictError(error)) {
        // Handled by MeetingScheduler
        return
      }

      // The referral was still created, just without the meeting, so treat it
      // as referred (to avoid duplicates) and explain
      if (errorTypeService.isMeetingNotBookedError(error)) {
        setCompanyIdsReferredToAlready([
          ...companyIdsReferredToAlready,
          compassPartner.id,
        ])
        hideModal()
        notyf.open({
          message: error.response?.data['hydra:description'] ?? '',
          type: 'warning',
          duration: 0,
          dismissible: true,
        })

        return
      }

      // Handle validation errors (e.g., the office can no longer take in-person meetings)
      const violationMessages =
        constraintViolationService.getViolationMessages(error)

      if (violationMessages.length > 0) {
        notyf.error(violationMessages.join(' '))
        return
      }

      handleReferralCreatedError()
    },
  })

  function handleReferralCreatedSuccess() {
    setCompanyIdsReferredToAlready([
      ...companyIdsReferredToAlready,
      compassPartner.id,
    ])

    notyf.open({
      message: isCreateReferral
        ? 'Your referral has been created'
        : 'Your callback request has been sent',
      className: 'max-w-none',
      type: 'success',
    })

    if (activeModal === MODAL_TYPE.bookMeetingNonCalendly) {
      hideModal()
    }
  }

  function handleReferralCreatedError() {
    notyf.open({
      type: 'danger',
      message: isCreateReferral
        ? 'Could not create referral'
        : 'Unable to process your callback request',
    })
  }

  function renderCta() {
    if (hasAlreadyBeenReferredTo) {
      return 'Callback requested'
    }

    return scheduleCallUrl || compassPartner.hasUsableCalendars
      ? 'Schedule a meeting'
      : 'Request a callback'
  }

  const isSchedulingCall = scheduleCallMutation.isPending

  function renderModal() {
    switch (activeModal) {
      case MODAL_TYPE.compassSharing:
        return (
          <ShareCompassReportModal
            company={compassPartner}
            onAnswer={(isSharing) => {
              setIsSharingCompassData(isSharing)
              showBookCallModal()
            }}
            onHide={hideModal}
          />
        )
      case MODAL_TYPE.bookMeetingViaScheduler: {
        const aboutYourClient = compassReport.jsonPayload?.input.aboutYourClient

        const clientFullName =
          aboutYourClient?.user_name_first && aboutYourClient.user_name_last
            ? `${aboutYourClient.user_name_first} ${aboutYourClient.user_name_last}`
            : currentClientUser?.fullName || ''

        const clientEmail =
          aboutYourClient?.user_email || currentClientUser?.email || ''

        return (
          <SchedulerModal
            onComplete={async (bookingDetails: MeetingDetails) => {
              return await scheduleCallMutation.mutateAsync({
                meeting: {
                  start: bookingDetails.start,
                  end: bookingDetails.end,
                  participants: bookingDetails.participants,
                  format: bookingDetails.format,
                  locationId: bookingDetails.locationId,
                  clientAddressLine1: bookingDetails.clientAddressLine1,
                  clientTownOrCity: bookingDetails.clientTownOrCity,
                  clientPostcode: bookingDetails.clientPostcode,
                },
              })
            }}
            onHide={hideModal}
            isProcessing={isSchedulingCall}
            primaryParticipant={{
              name: clientFullName,
              email: clientEmail,
            }}
            toCompanyName={compassPartner.presentationName}
            toCompanyId={compassPartner.id}
          />
        )
      }

      case MODAL_TYPE.requestCallback:
        return (
          <RequestCallbackModal
            company={compassPartner}
            callbackTelephone={
              isCreateReferral
                ? requestReferralCallbackMutation.data?.callbackTelephone
                : requestCallbackMutation.data?.callbackTelephone
            }
            onSubmit={(values: RequestCallbackFormValues) =>
              isCreateReferral
                ? requestReferralCallbackMutation.mutate(values)
                : requestCallbackMutation.mutate(values)
            }
            onHide={hideModal}
          />
        )
      case MODAL_TYPE.bookMeetingViaCalendly:
        return (
          <CalendlyModal
            companyName={compassPartner.presentationName}
            scheduleCallUrl={getOrFail(scheduleCallUrl)}
            onCallScheduled={() => scheduleCallMutation.mutate()}
            onHide={hideModal}
            isProcessing={isSchedulingCall}
          />
        )
      case MODAL_TYPE.bookMeetingNonCalendly:
        return (
          <NonCalendlyModal
            companyName={compassPartner.presentationName}
            scheduleCallUrl={getOrFail(scheduleCallUrl)}
            onBooked={() => scheduleCallMutation.mutate()}
            onHide={hideModal}
            isProcessing={isSchedulingCall}
          />
        )
      default:
        return null
    }
  }

  return (
    <>
      <Button
        onClick={handleCtaClick}
        disabled={hasAlreadyBeenReferredTo}
        onFocus={onHoverEnter}
        onMouseEnter={onHoverEnter}
        onMouseLeave={onHoverLeave}
        onBlur={onHoverLeave}
      >
        {renderCta()}
      </Button>

      {renderModal()}
    </>
  )
}

export default RequestCompanyCallbackButton
